/**
 * Data layer for `trackerity-referral-partner-program`. Thin typed fetch wrappers over /api/v1/trackerity-referral-partner-program.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface TrackerityReferralPartnerProgramRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListTrackerityReferralPartnerProgramResponse {
	count: number;
	items: TrackerityReferralPartnerProgramRow[];
}

export interface CreateTrackerityReferralPartnerProgramBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/trackerity-referral-partner-program";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListTrackerityReferralPartnerProgramResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListTrackerityReferralPartnerProgramResponse>(res);
}

export async function create(body: CreateTrackerityReferralPartnerProgramBody): Promise<TrackerityReferralPartnerProgramRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<TrackerityReferralPartnerProgramRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const trackerityReferralPartnerProgramQueryKeys = {
	all: ["trackerity-referral-partner-program"] as const,
	list: () => [...trackerityReferralPartnerProgramQueryKeys.all] as const,
	detail: (id: string) => [...trackerityReferralPartnerProgramQueryKeys.all, id] as const,
};
